/**
 * iOS/Android push through `@capacitor/push-notifications` (v8), loaded from the native
 * entry chunk (NativeLoaders.push). iOS yields an APNs device token, Android an FCM
 * registration token; both go to POST /v1/push/register with the platform
 * (`{ kind: 'apns', platform: 'ios', token }`, `{ kind: 'fcm', platform: 'android', token }`)
 * and again whenever the OS rotates the token. A token longer than the API takes (FCM 512,
 * APNs 200 characters) is refused here instead of being sent.
 *
 * The OS prompt only happens in requestPermission(), which the game calls after its
 * own soft ask (never on first launch). listen() belongs to startup: a tap on a
 * notification (the plugin keeps the one that launched the app until someone listens)
 * becomes a deep link, and a notification received in the foreground goes to the game. Android's
 * notification channels are not created here: the native app creates them in the device's
 * language at every start (PUSH_CHANNELS). refresh() belongs there too: APNs hands out a
 * token only after register() in the same process, so a device that registered once asks
 * again at every start, and a changed token (a restore onto a new device, an FCM rotation
 * while the app was not running) reaches the API.
 *
 * An Android build without Firebase config (no google-services.json) leaves the plugin out
 * (MainActivity), because its register() would crash the app. Every call then rejects with
 * Capacitor's UNIMPLEMENTED, which the permission reads report as `unsupported`, so the game
 * never offers notifications on such a build.
 */
import type { PushChannelId, PushRegister } from '@mp/schemas';
import type { z } from 'zod';
import type { ApiClient } from '../api.js';
import type { Loader } from '../lazy.js';
import { linkPath, type LinkConfig } from '../native/links.js';
import type { KV } from '../storage.js';
import type { PushPermission, PushProvider, PushSubscriptionInfo } from './types.js';

type Receive = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
interface Handle {
  remove(): Promise<void>;
}
/** PushNotificationSchema fields used here; `data` is the FCM data map / the APNs userInfo. */
export interface NativePushNotification {
  id: string;
  title?: string;
  body?: string;
  data: unknown;
  link?: string;
}
export interface NativePushChannel {
  id: string;
  /** shown in the system settings */
  name: string;
  description?: string;
  /** 3 = IMPORTANCE_DEFAULT (sound, no heads-up) */
  importance?: 0 | 1 | 2 | 3 | 4 | 5;
  visibility?: -1 | 0 | 1;
  vibration?: boolean;
}

/** The part of PushNotificationsPlugin used here, as in its definitions.d.ts. */
export interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: Receive }>;
  requestPermissions(): Promise<{ receive: Receive }>;
  register(): Promise<void>;
  unregister(): Promise<void>;
  createChannel(c: NativePushChannel): Promise<void>;
  addListener(event: 'registration', fn: (t: { value: string }) => void): Promise<Handle>;
  addListener(event: 'registrationError', fn: (e: { error: string }) => void): Promise<Handle>;
  addListener(event: 'pushNotificationReceived', fn: (n: NativePushNotification) => void): Promise<Handle>;
  addListener(event: 'pushNotificationActionPerformed', fn: (a: { actionId: string; notification: NativePushNotification }) => void): Promise<Handle>;
}
export interface PushNotificationsModule {
  PushNotifications: PushNotificationsPlugin;
}

/**
 * The channel ids and the type-to-channel map live in `@mp/schemas` (push-channels.ts),
 * because the API sends the channel in every FCM message and cannot import this package.
 * They are re-exported here so client code keeps one import for push.
 */
export { PUSH_CHANNEL_DEFAULT, PUSH_CHANNEL_IDS, PUSH_CHANNEL_OF, pushChannelOf, type PushChannelId } from '@mp/schemas';

/**
 * Android channels: the SY-03.1 push types grouped so a player can mute one kind in system
 * settings without losing the rest.
 *
 * The device creates them natively at every start, from localized string resources
 * (`apps/mobile/android/.../NotificationChannels.java`, with one `res/values-<lang>` file each),
 * because a notification can arrive before the WebView has booted and because a name created
 * from here would be in whatever language this bundle was built with. So these entries are the
 * id list — the ids the API must use and the English the Android strings were written from — not
 * the channels the app shows.
 */
export const PUSH_CHANNELS: readonly (NativePushChannel & { id: PushChannelId })[] = [
  { id: 'social', name: 'Friends', description: 'Help from friends', importance: 3 },
  { id: 'rewards', name: 'Rewards', description: 'Offline sales', importance: 3 },
  { id: 'events', name: 'Events', description: 'League reminders · New events', importance: 3 },
];

export const PUSH_REGISTER_ROUTE = '/v1/push/register';

/** POST /v1/push/register: the same call for a Web Push subscription and a native token. */
export async function registerPushSubscription(api: ApiClient, sub: PushSubscriptionInfo): Promise<void> {
  const body: z.input<typeof PushRegister> = sub;
  await api.call('POST', PUSH_REGISTER_ROUTE, body);
}

/** A notification the player tapped. `url` is an in-app path when `links` is configured. */
export interface PushOpen {
  actionId: string;
  url: string | null;
  /** server push type (offline_ready, friend_helped, …) */
  type: string | null;
  data: Record<string, unknown>;
}
/** A notification that arrived while the game was in the foreground. */
export interface PushMessage {
  title: string | null;
  body: string | null;
  url: string | null;
  type: string | null;
  data: Record<string, unknown>;
}
export interface PushHandlers {
  open?(o: PushOpen): void;
  receive?(m: PushMessage): void;
  /** a new or rotated token, already registered with the API */
  token?(sub: PushSubscriptionInfo): void;
  error?(e: Error): void;
}

export interface NativePushOptions {
  platform: 'ios' | 'android';
  load: Loader<PushNotificationsModule>;
  /** registers every token with the API (push/register) */
  api?: ApiClient;
  /** remembers that this device registered, for refresh() at the next start */
  kv?: KV;
  links?: LinkConfig;
  /**
   * Extra Android channels to create, or replacements for the native ones with names the host
   * has already localized. Unset, the channels the server sends on are the native ones.
   */
  channels?: readonly NativePushChannel[];
  /** how long subscribe() waits for the OS to hand out a token */
  tokenTimeoutMs?: number;
}

/** set once a token of this device reached the API; unsubscribe() clears it */
export const PUSH_REGISTERED_KEY = 'push.native.registered';

/** The longest token POST /v1/push/register accepts (PushRegister). */
export const PUSH_TOKEN_MAX = { fcm: 512, apns: 200 } as const;

const mapPermission = (r: Receive): PushPermission => (r === 'granted' || r === 'denied' ? r : 'prompt');
/** CapacitorException code of a call to a plugin the native app does not register */
const UNIMPLEMENTED = 'UNIMPLEMENTED';
const absentPlugin = (e: unknown): PushPermission => {
  if ((e as { code?: unknown } | null)?.code === UNIMPLEMENTED) return 'unsupported';
  throw e;
};
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export class NativePushProvider implements PushProvider {
  readonly kind = 'native';
  private channels: Promise<void> | null = null;
  /** the token last sent to the API, and that call */
  private registered: { token: string; done: Promise<void> } | null = null;

  constructor(private readonly o: NativePushOptions) {}

  /** `unsupported` when the app has no push plugin (an Android build without Firebase config). */
  async permissionState(): Promise<PushPermission> {
    const { PushNotifications } = await this.o.load();
    return PushNotifications.checkPermissions().then((r) => mapPermission(r.receive), absentPlugin);
  }

  /** Shows the OS prompt (iOS; Android 13+). Call only after the game's own soft ask. */
  async requestPermission(): Promise<PushPermission> {
    const { PushNotifications } = await this.o.load();
    return PushNotifications.requestPermissions().then((r) => mapPermission(r.receive), absentPlugin);
  }

  async subscribe(): Promise<PushSubscriptionInfo | null> {
    if ((await this.permissionState()) !== 'granted') return null;
    const { PushNotifications } = await this.o.load();
    await this.ensureChannels(PushNotifications);
    let resolve!: (token: string) => void;
    let reject!: (e: Error) => void;
    const token = new Promise<string>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const handles = await Promise.all([
      PushNotifications.addListener('registration', (t) => resolve(t.value)),
      PushNotifications.addListener('registrationError', (e) => reject(new Error(e.error))),
    ]);
    const timer = setTimeout(() => reject(new Error('push registration timed out')), this.o.tokenTimeoutMs ?? 20_000);
    try {
      await PushNotifications.register();
      return await this.adopt(await token);
    } finally {
      clearTimeout(timer);
      await Promise.all(handles.map((h) => h.remove()));
    }
  }

  /** Stops delivery to this device (FCM token deleted / APNs unregistered). */
  async unsubscribe(): Promise<void> {
    const { PushNotifications } = await this.o.load();
    await PushNotifications.unregister();
    this.registered = null;
    this.o.kv?.remove(PUSH_REGISTERED_KEY);
  }

  /**
   * Startup: send the current token again when this device registered before and the OS
   * still allows notifications; null otherwise. Never prompts, and never registers a device
   * whose player has not opted in (Android 12 and older grant the permission at install).
   */
  async refresh(): Promise<PushSubscriptionInfo | null> {
    if (this.o.kv?.get(PUSH_REGISTERED_KEY) !== '1') return null;
    return this.subscribe();
  }

  /** Startup: taps, foreground notifications and token rotation. Resolves to the unsubscribe. */
  async listen(h: PushHandlers): Promise<() => Promise<void>> {
    const { PushNotifications } = await this.o.load();
    await this.ensureChannels(PushNotifications);
    const handles = await Promise.all([
      PushNotifications.addListener('pushNotificationActionPerformed', (a) => h.open?.({ actionId: a.actionId, ...this.message(a.notification) })),
      PushNotifications.addListener('pushNotificationReceived', (n) => {
        const m = this.message(n);
        h.receive?.({ title: str(n.title), body: str(n.body), ...m });
      }),
      PushNotifications.addListener('registration', (t) => void this.adopt(t.value).then((sub) => h.token?.(sub), (e: unknown) => h.error?.(e instanceof Error ? e : new Error(String(e))))),
      PushNotifications.addListener('registrationError', (e) => h.error?.(new Error(e.error))),
    ]);
    return async () => {
      await Promise.all(handles.map((x) => x.remove()));
    };
  }

  /** Register a token with the API once: subscribe() and the rotation listener see the same token at the same time. */
  private async adopt(token: string): Promise<PushSubscriptionInfo> {
    const sub: PushSubscriptionInfo = this.o.platform === 'ios' ? { kind: 'apns', platform: 'ios', token } : { kind: 'fcm', platform: 'android', token };
    if (token.length > PUSH_TOKEN_MAX[sub.kind]) throw new Error(`push: the ${sub.kind} token has ${token.length} characters; the API takes at most ${PUSH_TOKEN_MAX[sub.kind]}`);
    if (!this.o.api) return sub;
    let reg = this.registered;
    if (reg?.token !== token) {
      const mine = (reg = { token, done: registerPushSubscription(this.o.api, sub) });
      this.registered = mine;
      mine.done.catch(() => {
        if (this.registered === mine) this.registered = null; // a failed call is retried at the next sighting
      });
    }
    await reg.done;
    this.o.kv?.set(PUSH_REGISTERED_KEY, '1');
    return sub;
  }

  private message(n: NativePushNotification): { url: string | null; type: string | null; data: Record<string, unknown> } {
    const data = isRecord(n.data) ? n.data : {};
    const raw = str(data.url) ?? str(data.link) ?? str(n.link);
    return { url: raw !== null && this.o.links ? linkPath(raw, this.o.links) : raw, type: str(data.type), data };
  }

  /**
   * The channels the server sends on are created natively at every start, in the device's
   * language (PUSH_CHANNELS). Creating them again from here would overwrite those names with
   * this bundle's language, so only channels the host passes deliberately are created, and
   * nothing happens on a build that passes none. Android 7 has no channels at all and rejects
   * the call, which is why failures are swallowed.
   */
  private ensureChannels(p: PushNotificationsPlugin): Promise<void> {
    const channels = this.o.channels;
    if (this.o.platform !== 'android' || channels === undefined || channels.length === 0) return Promise.resolve();
    this.channels ??= Promise.all(channels.map((c) => p.createChannel(c))).then(
      () => undefined,
      () => undefined,
    );
    return this.channels;
  }
}
